import type {
  CheckinVersion,
  ContactRole,
  ContactSubrole,
  CoproApprovalStatus,
  DesignStatus,
  ImplementationTypology,
  InseeFullDemographics,
  LaunchClearanceStatus,
  LeaseContractType,
  NetworkDashboardStoreAgeFilter,
  PersonReference,
  ProjectTypology,
  RealEstateKpis,
  SecurisationType,
  StoreCaOverDays,
} from '@gozoki/api-types'
import { gozokiApiWithAuth } from '../gozokiApi'

// Lowercase status format as returned by the API
export type StoreProjectApiStatus =
  | 'before_project'
  | 'under_study'
  | 'advanced_project'
  | 'secured'
  | 'works'
  | 'openable'
  | 'open'

export interface DocumentItem {
  url: string
  name: string
  size: number
  uploadedAt: string
}

export interface StoreProjectWorksResponse {
  workDurationWeeks: number | null
  keyReceptionDate: string | null
  workStartDate: string | null
  workEndDate: string | null
  openingDate: string | null
  moeExecution: PersonReference
  projectManagerAdminId: number | null
  fitter: PersonReference
  preliminaryVisitDate: string | null
  technicalAuditTodoDate: string | null
  technicalAuditPlannedDate: string | null
  technicalAuditDoneDate: string | null
  proDceTodoDate: string | null
  proDceStartedDate: string | null
  proDceDoneDate: string | null
  proDceTargetDate: string | null
  validatedGoBudget: number | null
  dgdBudget: number | null
}

export interface StoreProjectLeaseResponse {
  projectTypology: ProjectTypology | null
  implementationTypology: ImplementationTypology | null
  contractType: LeaseContractType | null
  totalFloorArea: number | null
  salesFloorArea: number | null
  backOfficeFloorArea: number | null
  glaSurface: number | null
  expectedSigningDate: string | null
  loiValidationDate: string | null
  promiseProjectedDate: string | null
  promiseSignatureDate: string | null
  signatureDate: string | null
  securisation: SecurisationType | null
  leaseStartDate: string | null
  coproApproval: CoproApprovalStatus | null
}

export interface StoreProjectDesignResponse {
  designRequestDate: string | null
  designStatus: DesignStatus | null
  fridgeCountX: number | null
  fridgeCountY: number | null
  fridgeCountZ: number | null
  checkoutCount: number | null
  checkinVersion: CheckinVersion | null
  validatedPlanUrl: string | null
  screen24Gourmandise: number | null
  screen26Gourmandise: number | null
  screen24Cafe: number | null
  screen26Cafe: number | null
  screen26Fridges: number | null
  externalScreens: number | null
}

export interface StoreProjectPermitsResponse {
  designMoe: PersonReference
  dpFilingDate: string | null
  dpObtainedDate: string | null
  atFilingDate: string | null
  atObtainedDate: string | null
  deFilingDate: string | null
  deObtainedDate: string | null
  launchClearance: LaunchClearanceStatus | null
}

export interface StoreProjectResponse {
  id: string
  name: string
  devId: string | null
  dev: number | null
  address: string
  city: string
  postalCode: string
  surfaceArea: number
  score: number
  status: StoreProjectApiStatus
  isAbandoned: boolean
  rentPerYear: number | null
  works: number | null
  leaseFunds: number | null
  caPredictionYear1: number | null
  caPredictionYear2: number | null
  otherCosts: string | null
  storeName: string | null
  storeRef?: string | null
  contacts: {
    id: string
    name: string
    email?: string
    phone?: string
    contactRole?: ContactRole | null
    contactSubrole?: ContactSubrole | null
  }[]
  googleMapsUrl: string | null
  latitude: number | null
  longitude: number | null
  photoUrls: string[]
  documents: DocumentItem[]
  comment: string
  createdAt: string
  updatedAt: string
  demographics: InseeFullDemographics | null
  worksData: StoreProjectWorksResponse | null
  leaseData: StoreProjectLeaseResponse | null
  designData: StoreProjectDesignResponse | null
  permitsData: StoreProjectPermitsResponse | null
}

interface LegacyOrCurrentRealEstateKpisResponse {
  avgLandCostEur?: number | null
  avgLandCostKeur?: number | null
  avgLandCostPercentage?: number | null
  avgLandCostKeurPercentage?: number | null
  avgRentEur?: number | null
  avgWorksBudgetEur?: number | null
  avgWorksBudgetKeur?: number | null
  avgWorksBudgetPercentage?: number | null
  avgWorksBudgetKeurPercentage?: number | null
  openStoresDelta?: number | null
  openStoresDelta30d?: number | null
  averageAnnualCaEur?: number | null
  averageAnnualCa?: number | null
  averageAnnualCaMatureEur?: number | null
}

function toEurFromPossibleKeur(valueEur?: number | null, valueKeur?: number | null): number | null {
  if (valueEur !== undefined) {
    return valueEur ?? null
  }
  if (valueKeur !== undefined) {
    return valueKeur === null ? null : valueKeur * 1000
  }
  return null
}

// Fetch all store projects
export const fetchStoreProjects = async () => {
  const response = await gozokiApiWithAuth.get<StoreProjectResponse[]>('/store-projects')
  return response.data
}

export interface FetchRealEstateKpisParams {
  /** Days to look back for open-stores delta (backend default 30). */
  openStoresComparisonDays?: number
  /** Restrict KPIs to a specific list of stores (joined via `stores.ref`). */
  storeIds?: number[]
  /** Restrict KPIs to stores matching the dashboard age preset. */
  storeAgeFilter?: NetworkDashboardStoreAgeFilter
  /** Period start (YYYY-MM-DD). With `endDate`, switches the backend to the selection + period path. */
  startDate?: string
  /** Period end (YYYY-MM-DD). Presence triggers the period-aware (selection + period) KPIs. */
  endDate?: string
}

// Fetch real estate KPIs (valeur moyenne + coût travaux moyen with % diff vs N-1)
export const fetchRealEstateKpis = async (params?: FetchRealEstateKpisParams) => {
  const queryParams: Record<string, string | number> = {}
  if (params?.openStoresComparisonDays !== undefined) {
    queryParams.openStoresComparisonDays = params.openStoresComparisonDays
  }
  if (params?.storeIds && params.storeIds.length > 0) {
    queryParams.storeIds = params.storeIds.join(',')
  }
  if (params?.storeAgeFilter !== undefined) {
    queryParams.storeAgeFilter = params.storeAgeFilter
  }
  if (params?.startDate !== undefined) {
    queryParams.startDate = params.startDate
  }
  if (params?.endDate !== undefined) {
    queryParams.endDate = params.endDate
  }
  const response = await gozokiApiWithAuth.get<LegacyOrCurrentRealEstateKpisResponse>(
    '/store-projects/kpis',
    {
      params: Object.keys(queryParams).length > 0 ? queryParams : undefined,
    },
  )
  const data = response.data

  const normalizedResponse: RealEstateKpis = {
    avgLandCostEur: toEurFromPossibleKeur(data.avgLandCostEur, data.avgLandCostKeur),
    avgLandCostPercentage: data.avgLandCostPercentage ?? data.avgLandCostKeurPercentage ?? null,
    avgRentEur: data.avgRentEur ?? null,
    avgWorksBudgetEur: toEurFromPossibleKeur(data.avgWorksBudgetEur, data.avgWorksBudgetKeur),
    avgWorksBudgetPercentage:
      data.avgWorksBudgetPercentage ?? data.avgWorksBudgetKeurPercentage ?? null,
    openStoresDelta: data.openStoresDelta ?? data.openStoresDelta30d ?? null,
    averageAnnualCaEur:
      data.averageAnnualCaEur ?? (data.averageAnnualCa !== undefined ? data.averageAnnualCa : null),
    averageAnnualCaMatureEur: data.averageAnnualCaMatureEur ?? null,
  }

  return normalizedResponse
}

export const fetchStoreCaOverDays = async ({
  storeRef,
  days,
}: {
  storeRef: string
  days: number
}): Promise<StoreCaOverDays> => {
  const response = await gozokiApiWithAuth.get<StoreCaOverDays>(
    `/store-projects/stores/${encodeURIComponent(storeRef)}/ca/${days}`,
  )
  return response.data
}
